import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import useFetch from "../../../Component/Hooks/useFetch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeDropper } from "@fortawesome/free-solid-svg-icons";
import ColorPicker from "../../../Component/ColorPicker/ColorPicker";
import Modal from "../../../Component/Modal/Modal";
import IframeCode from "./IframeCode";
import CarLoader from "../Loader/CarLoader";
import FormField from "./FormField";

const decToStr = (v) => {
  if (v == null) return "";
  if (typeof v === "object" && v.$numberDecimal != null)
    return v.$numberDecimal;
  return String(v);
};

const EditWidget = ({ widgetId }) => {
  const user = useSelector((state) => state.userReducer);
  const userId = user.user._id;

  const [isLoading, setIsLoading] = useState(false);
  const [formChanged, setFormChanged] = useState(false);
  const [iframeCode, setIframeCode] = useState(false);
  const [formData, setFormData] = useState(null);

  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [colorPickers, setColorPickers] = useState({
    backgroundColor: false,
    btnColor: false,
    labelColor: false,
  });

  const { loading, data } = useFetch(
    `${process.env.REACT_APP_API_URL}/api/widgets/taxi/getOneWidget/${widgetId}`
  );

  useEffect(() => {
    if (!data?.widget) return;
    const w = data.widget;
    setFormData({
      title: w.title || "",
      pricePerKmDay: decToStr(w.pricePerKmDay),
      pricePerKmNight: decToStr(w.pricePerKmNight),
      pricePerKmSunday: decToStr(w.pricePerKmSunday),
      pricePerKmRoundTripDay: decToStr(w.pricePerKmRoundTripDay),
      pricePerKmRoundTripSunday: decToStr(w.pricePerKmRoundTripSunday),
      pricePerPassenger: decToStr(w.pricePerPassenger),
      pricePerBaggage: decToStr(w.pricePerBaggage),
      pricePriseEnCharge: decToStr(w.pricePriseEnCharge),
      flatFareEnabled: !!w.flatFareEnabled,
      flatFareKmThreshold: decToStr(w.flatFareKmThreshold),
      flatFareAmount: decToStr(w.flatFareAmount),
      flatFareBeyondKmPrice: decToStr(w.flatFareBeyondKmPrice),
      contactMail: w.contactMail || "",
      contactPhone: w.contactPhone || "",
      allerRetourIsActive:
        w.allerRetourIsActive === undefined ? true : !!w.allerRetourIsActive,
      isOnline: w.isOnline || "online",
      type: w.type || "Widget Taxi",
      btnColor: w.btnColor || "",
      backgroundColor: w.backgroundColor || "",
      labelColor: w.labelColor || "",
    });
  }, [data, widgetId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormChanged(true);
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    const v = value === "true" ? true : value === "false" ? false : value;
    setFormData((prev) => ({ ...prev, [name]: v }));
    setFormChanged(true);
  };

  const handleModeChange = (flatFareEnabled) => {
    setFormData((prev) => ({ ...prev, flatFareEnabled }));
    setFormChanged(true);
  };

  const handleColorPickerToggle = (fieldName) => {
    setColorPickers((prev) =>
      Object.keys(prev).reduce((acc, key) => {
        acc[key] = key === fieldName ? !prev[key] : false;
        return acc;
      }, {})
    );
    setActiveColorPicker(fieldName);
  };

  const handleColorChange = (color) => {
    setFormData((prev) => ({ ...prev, [activeColorPicker]: color.hex }));
    setFormChanged(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData) return;
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/widgets/taxi/edite/${widgetId}/user/${userId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        toast.success("Widget mis à jour");
        setIframeCode(true);
        setFormChanged(false);
      } else if (!user.user.isPremium) {
        toast.error("Vous n'êtes pas premium");
      } else {
        toast.error("Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Une erreur est survenue");
    }
    setIsLoading(false);
  };

  if (loading || !formData) {
    return (
      <div className="p-4 w-full flex justify-center mt-10">
        <CarLoader />
      </div>
    );
  }

  if (!user.user.isPremium) {
    return (
      <div className="p-4 w-full">
        <p className="text-gray-500 text-sm text-center mt-10">
          Vous n'êtes pas autorisé.
        </p>
      </div>
    );
  }

  const isFlat = formData.flatFareEnabled;
  const inputClass =
    "text-center block w-full rounded-lg border border-gray-200 p-2.5 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all";

  return (
    <div className="p-4 w-full">
      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* ── Section 1 : Informations générales ── */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Informations générales
            </p>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Titre du widget
              </label>
              <input
                className={inputClass}
                type="text"
                name="title"
                placeholder="Ex : VTC Paris — Gare du Nord"
                value={formData.title}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Afficher l'aller-retour ?
              </label>
              <select
                className={inputClass}
                name="allerRetourIsActive"
                value={String(formData.allerRetourIsActive)}
                onChange={handleSelectChange}
              >
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </select>
            </div>
          </div>

          {/* ── Section 2 : Tarification ── */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Tarification
            </p>

            <div className="inline-flex p-1 bg-white border border-gray-200 rounded-lg w-fit">
              <ModeButton
                active={!isFlat}
                onClick={() => handleModeChange(false)}
              >
                Au kilomètre
              </ModeButton>
              <ModeButton
                active={isFlat}
                onClick={() => handleModeChange(true)}
              >
                Forfait
              </ModeButton>
            </div>

            {!isFlat ? (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField
                    label="Aller simple — jour"
                    hint="7h–19h, lun.–sam."
                    name="pricePerKmDay"
                    suffix="€/km"
                    value={formData.pricePerKmDay}
                    onChange={handleInputChange}
                  />
                  <FormField
                    label="Aller simple — nuit"
                    hint="19h–7h + dim."
                    name="pricePerKmNight"
                    suffix="€/km"
                    value={formData.pricePerKmNight}
                    onChange={handleInputChange}
                  />
                </div>
                {formData.allerRetourIsActive && (
                  <div className="flex flex-col gap-1.5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <FormField
                        label="Aller-retour — jour"
                        hint="7h–19h, lun.–sam."
                        name="pricePerKmRoundTripDay"
                        suffix="€/km"
                        placeholder="0.00"
                        value={formData.pricePerKmRoundTripDay}
                        onChange={handleInputChange}
                      />
                      <FormField
                        label="Aller-retour — nuit"
                        hint="19h–7h + dim."
                        name="pricePerKmRoundTripSunday"
                        suffix="€/km"
                        placeholder="0.00"
                        value={formData.pricePerKmRoundTripSunday}
                        onChange={handleInputChange}
                      />
                    </div>
                    <p className="text-xs text-gray-400">
                      Optionnel · appliqué sur la distance aller + retour. Laissez
                      vide pour utiliser le tarif aller simple.
                    </p>
                  </div>
                )}
                <FormField
                  label="Prise en charge"
                  hint="Ajoutée à chaque course"
                  name="pricePriseEnCharge"
                  suffix="€"
                  value={formData.pricePriseEnCharge}
                  onChange={handleInputChange}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <FormField
                    label="Forfait"
                    name="flatFareAmount"
                    suffix="€"
                    placeholder="25"
                    value={formData.flatFareAmount}
                    onChange={handleInputChange}
                    required
                  />
                  <FormField
                    label="Jusqu'à"
                    name="flatFareKmThreshold"
                    suffix="km"
                    placeholder="10"
                    step="1"
                    value={formData.flatFareKmThreshold}
                    onChange={handleInputChange}
                    required
                  />
                  <FormField
                    label="Au-delà"
                    name="flatFareBeyondKmPrice"
                    suffix="€/km"
                    placeholder="2"
                    value={formData.flatFareBeyondKmPrice}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong className="text-amber-900">Aperçu :</strong>{" "}
                    jusqu'à{" "}
                    <strong>{formData.flatFareKmThreshold || "?"} km</strong> ={" "}
                    <strong>{formData.flatFareAmount || "?"} €</strong> ·
                    au-delà ={" "}
                    <strong>{formData.flatFareBeyondKmPrice || "?"} €/km</strong>{" "}
                    (×2 si aller-retour)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── Section 3 : Suppléments ── */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Suppléments
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField
                label="Par passager"
                hint="Dès le 5e"
                name="pricePerPassenger"
                suffix="€"
                value={formData.pricePerPassenger}
                onChange={handleInputChange}
              />
              <FormField
                label="Par bagage"
                hint="Au-delà de 3"
                name="pricePerBaggage"
                suffix="€"
                value={formData.pricePerBaggage}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* ── Section 4 : Apparence ── */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Apparence
            </p>
            <div className="-mx-3 flex flex-wrap">
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur de fond"
                  color={formData.backgroundColor}
                  onColorChange={(color) => handleColorChange(color)}
                  isOpen={colorPickers.backgroundColor}
                  togglePicker={() =>
                    handleColorPickerToggle("backgroundColor")
                  }
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur des boutons"
                  color={formData.btnColor}
                  onColorChange={(color) => handleColorChange(color)}
                  isOpen={colorPickers.btnColor}
                  togglePicker={() => handleColorPickerToggle("btnColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur du texte"
                  color={formData.labelColor}
                  onColorChange={(color) => handleColorChange(color)}
                  isOpen={colorPickers.labelColor}
                  togglePicker={() => handleColorPickerToggle("labelColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
            </div>
          </div>

          {/* ── Section 5 : Contact ── */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Contact
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Email
                </label>
                <input
                  className={inputClass}
                  type="email"
                  name="contactMail"
                  placeholder="contact@exemple.fr"
                  value={formData.contactMail}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Téléphone
                </label>
                <input
                  className={inputClass}
                  type="tel"
                  pattern="[0-9]{10}"
                  name="contactPhone"
                  placeholder="0612345678"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* ── Submit ── */}
          <button
            type="submit"
            disabled={isLoading || !formChanged}
            className="w-full p-3 rounded-xl text-sm font-semibold text-white bg-black hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading
              ? "Enregistrement…"
              : formChanged
              ? "Enregistrer"
              : "Aucune modification"}
          </button>
        </form>
      </div>

      {iframeCode && (
        <Modal
          title="Code iframe"
          isOpen={true}
          onClose={() => setIframeCode(false)}
          children={<IframeCode widgetId={widgetId} />}
        />
      )}
    </div>
  );
};

const ModeButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={
      active
        ? "px-4 py-1.5 text-xs font-semibold rounded-md bg-black text-white transition-colors"
        : "px-4 py-1.5 text-xs font-semibold rounded-md text-gray-500 hover:text-gray-900 transition-colors"
    }
  >
    {children}
  </button>
);

export default EditWidget;
